// Components
import React, { useCallback, useState } from 'react';
import { WpButton } from 'Wpds/components/button';
import { WpModal, WpModalHeader, WpModalBody } from 'Wpds/components/modal';
import { WpText } from 'Wpds/components/text';
import { WpTextField } from 'Wpds/components/text-field';
import { WpForm, WpFormLayout } from 'Wpds/components/form';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpCard, WpCardBody } from 'Wpds/components/card';
import ConfirmModal from 'Shared/components/ConfirmModal/ConfirmModal';

// Hooks
import { useMeetingBot } from 'Shared/utils/hooks/use-meeting-bot';

// Constants
import ColorConstants from 'Wpds/colors/ColorConstants';

// Styles
import './PiCompanionPanel.scss';
import { useToggle } from 'Shared/utils/hooks';

const renderCard = (isInProgress: boolean, handleBotAction: () => void, isChangingBotState: boolean): JSX.Element => {
  const baseClassName = 'PiCompanionPanelContainer';
  const header = (
    <div className={`${baseClassName}__header`}>
      <WpIcon name="magic" size="xl" color={ColorConstants.purple[800]} />
      <WpText variant="bodyMd" bold noMargin>
        PI Companion bot
      </WpText>
    </div>
  );

  const body = isInProgress ? (
    <>
      <WpText color="gray-800" variant="bodySm" noMargin>
        PI Companion will provide a summary, key decisions, and suggested action items at the end of the meeting.
      </WpText>
      <WpButton color="gray" variant="secondary" onClick={handleBotAction} loading={isChangingBotState}>
        Disable PI Companion
      </WpButton>
      <WpText color="gray-800" variant="bodySm" italic>
        To stop recording, you can remove the bot from your video meeting participants or click “Disable PI Companion”
        above.
      </WpText>
    </>
  ) : (
    <>
      <WpText color="gray-800" variant="bodySm">
        Invite PI Companion to your video meeting. It will generate a summary, key decisions, and suggested action items
        after the meeting.
      </WpText>
      <WpButton variant="secondary" onClick={handleBotAction} loading={isChangingBotState}>
        Invite PI Companion
      </WpButton>
    </>
  );

  return (
    <WpCard accent={ColorConstants.purple[800]}>
      <WpCardBody>
        {header}
        <div className={`${baseClassName}__body`}>{body}</div>
      </WpCardBody>
    </WpCard>
  );
};

const PiCompanionPanel: React.FC = () => {
  const { invite: inviteBot, remove: removeBot, bot, meetingBotIsInProgress, isMeetingBotEnabled } = useMeetingBot();

  const [isZoomMeetingURLModalOpen, setIsZoomMeetingURLModalOpen] = useState(false);
  const [isChangingBotState, setIsChangingBotState] = useState(false);
  const [isDisableBotModalOpen, toggleDisableBotModalOpen] = useToggle(false);

  const handleBotButtonClick = useCallback(() => {
    setIsZoomMeetingURLModalOpen(true);
  }, []);

  const handleInviteBot = useCallback(
    (meetingUrl: string) => {
      setIsChangingBotState(true);
      inviteBot(meetingUrl).then(() => {
        setIsZoomMeetingURLModalOpen(false);
        setIsChangingBotState(false);
      });
    },
    [inviteBot]
  );

  const handleRemoveBot = useCallback(() => {
    setIsChangingBotState(true);
    removeBot(bot.recallBotId).then(() => {
      setIsChangingBotState(false);
      toggleDisableBotModalOpen();
    });
  }, [removeBot, bot]);

  if (!isMeetingBotEnabled) return null;

  return (
    <div className="PiCompanionPanelContainer">
      {renderCard(
        meetingBotIsInProgress,
        meetingBotIsInProgress ? toggleDisableBotModalOpen : handleBotButtonClick,
        isChangingBotState
      )}

      {isZoomMeetingURLModalOpen && (
        <WpModal isOpen={isZoomMeetingURLModalOpen} onToggle={() => setIsZoomMeetingURLModalOpen(false)} size="sm">
          <WpModalHeader>PI Companion</WpModalHeader>
          <WpModalBody>
            <WpForm
              id="invite-bot-form"
              onSubmit={data => {
                const meetingUrl = data.meeting_url;
                handleInviteBot(meetingUrl);
              }}
            >
              <WpFormLayout direction="vertical">
                <WpText>PI Companion can help you take notes for your agenda items while using Meeting Mode.</WpText>
                <WpText>Please enter the URL of the meeting you would like PI Companion to join.</WpText>
                <WpTextField
                  id="meeting_url"
                  label="Meeting URL"
                  disabled={isChangingBotState}
                  required
                  showRequiredIndicator
                />
                <WpButton btnType="submit" variant="primary" loading={isChangingBotState} block>
                  Enable PI Companion
                </WpButton>
              </WpFormLayout>
            </WpForm>
          </WpModalBody>
        </WpModal>
      )}

      {isDisableBotModalOpen && (
        <ConfirmModal
          onToggle={toggleDisableBotModalOpen}
          isOpen={isDisableBotModalOpen}
          onConfirmClick={handleRemoveBot}
          isProcessing={isChangingBotState}
        >
          <WpText bold tag="span">
            This will remove PI Companion from your meeting participants and stop the recording.&nbsp;
          </WpText>
          <WpText tag="span">
            You&apos;ll see this summary and any actions you added in your workspace History tab.
          </WpText>
        </ConfirmModal>
      )}
    </div>
  );
};

export default PiCompanionPanel;
